// src/planner/AddEventModal.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ActionButton, EditInput, EditTextarea, MoveSelect } from './plannerStyles';
import { color, font } from '../../theme/tokens';

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background-color: rgba(7, 2, 5, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    z-index: 1001;
    opacity: ${props => props.$isOpen ? 1 : 0};
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
    transition: opacity 0.3s ease;
`;

const ModalContent = styled.div`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: 520px;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    padding: 32px 36px;
    background: ${color.surface};
    border: 1px solid ${color.line};
    color: ${color.text};
    display: flex;
    flex-direction: column;
    gap: 20px;
    transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.97)'};
    transition: transform 0.3s ease;

    /* corner ticks */
    &::before, &::after {
        content: '';
        position: absolute;
        width: 14px;
        height: 14px;
        pointer-events: none;
    }
    &::before { left: -1px; top: -1px; border-left: 1px solid ${color.blood}; border-top: 1px solid ${color.blood}; }
    &::after { right: -1px; bottom: -1px; border-right: 1px solid ${color.blood}; border-bottom: 1px solid ${color.blood}; }

    form { display: flex; flex-direction: column; gap: 18px; }

    @media (max-width: 640px) { padding: 22px 18px; }
`;

const ModalTitle = styled.h2`
    margin: 0;
    padding-bottom: 14px;
    border-bottom: 1px solid ${color.line};
    font-family: ${font.display};
    font-weight: 800;
    font-size: 22px;
    letter-spacing: 0.2em;
    color: ${color.text};
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 4px;
    button { min-height: 40px; padding: 0 20px; font-size: 14px; }
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    label {
        font-size: 13px;
        letter-spacing: 0.2em;
        color: ${color.text2};
    }
`;

function AddEventModal({ isOpen, onClose, onSubmit, eventToEdit, allEvents }) {
    const [formData, setFormData] = useState({ title: '', description: '', parentId: null, dueDate: '' });

    useEffect(() => {
        if (eventToEdit) {
            setFormData({
                title: eventToEdit.title || '',
                description: eventToEdit.description || '',
                parentId: eventToEdit.parent_id || null,
                dueDate: eventToEdit.due_date ? new Date(new Date(eventToEdit.due_date).getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().slice(0, 16) : ''
            });
        } else {
            setFormData({ title: '', description: '', parentId: null, dueDate: '' });
        }
    }, [eventToEdit, isOpen]);    

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const getPossibleParents = () => {
        if (!eventToEdit) return allEvents;
        const getDescendantIds = (event, eventList) => {
            let ids = new Set([event.id]);
            const children = eventList.filter(e => e.parent_id === event.id);
            children.forEach(child => getDescendantIds(child, eventList).forEach(id => ids.add(id)));
            return ids;
        };
        const descendantIds = getDescendantIds(eventToEdit, allEvents);
        return allEvents.filter(e => !descendantIds.has(e.id));
    };

    return (
        <ModalOverlay $isOpen={isOpen} onClick={onClose}>
            <ModalContent $isOpen={isOpen} role="dialog" aria-modal="true" aria-labelledby="planner-modal-title" onClick={e => e.stopPropagation()}>
                <ModalTitle id="planner-modal-title">{eventToEdit ? '编辑事项' : '新建事项'}</ModalTitle>
                <form onSubmit={handleSubmit}>
                    <FormField>
                        <label htmlFor="planner-title">标题</label>
                        <EditInput id="planner-title" type="text" name="title" value={formData.title} onChange={handleChange} required placeholder="事项名称" />
                    </FormField>
                    <FormField>
                        <label htmlFor="planner-description">描述</label>
                        <EditTextarea id="planner-description" name="description" value={formData.description} onChange={handleChange} placeholder="补充说明…" />
                    </FormField>
                    <FormField>
                        <label htmlFor="planner-parent">上级事项</label>
                        <MoveSelect id="planner-parent" name="parentId" value={formData.parentId || 'root'} onChange={e => handleChange({ target: { name: 'parentId', value: e.target.value === 'root' ? null : parseInt(e.target.value) }})}>
                            <option value="root">（无，作为根事项）</option>
                            {getPossibleParents().map(event => <option key={event.id} value={event.id}>{event.title}</option>)}
                        </MoveSelect>
                    </FormField>
                    <FormField>
                        <label htmlFor="planner-due">截止时间</label>
                        <EditInput id="planner-due" type="datetime-local" name="dueDate" value={formData.dueDate} onChange={handleChange} />
                    </FormField>
                </form>
                <ModalActions>
                    <ActionButton type="button" onClick={onClose}>取消</ActionButton>
                    <ActionButton type="button" $primary onClick={handleSubmit}>
                        保存
                    </ActionButton>
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
}

export default AddEventModal;